import {
    WebGLRenderer,
    Scene,
    PerspectiveCamera,
    ShaderMaterial,
    Color,
    BufferAttribute,
    BufferGeometry,
    Points
} from 'three';

class FabricHeroWave {

    constructor() {
        this.body = document.body;
        this.isVisible = false;

        this.distance = 100;
        this.amount_x = 50;
        this.amount_y = 50;
        this.count = 0;
        this.cameraX = 250;
        this.cameraY = 300;

        this.themes = {
            'default': {
                color: new Color( 0xFF86E9 ),
                alpha: 0.15
            },
            'light': {
                color: new Color( 0xE4E6EA ),
                alpha: 0.5
            }
        }

        this.bindMethods();
        this.initCanvas();
        this.initWebGL();
        this.initScene();
        this.initEvents();
        this.onResize();
    }

    init() {
        this.numParticles = this.amount_x * this.amount_y;
        this.positions = new Float32Array( this.numParticles * 3 );
        this.scales = new Float32Array( this.numParticles );

        let i = 0, j = 0;
        for ( let ix = 0; ix < this.amount_x; ix ++ ) {
            for ( let iy = 0; iy < this.amount_y; iy ++ ) {
                this.positions[ i ] = ix * this.distance - ( ( this.amount_x * this.distance ) / 2 );
                this.positions[ i + 1 ] = 0;
                this.positions[ i + 2 ] = iy * this.distance - ( ( this.amount_y * this.distance ) / 2 );
                this.scales[ j ] = 1;
                i += 3;
                j ++;
            }
        }

        const geometry = new BufferGeometry();
        geometry.setAttribute( 'position', new BufferAttribute( this.positions, 3 ) );
        geometry.setAttribute( 'scale', new BufferAttribute( this.scales, 1 ) );

        const material = new ShaderMaterial( {
            transparent: true,
            uniforms: {
                color: { value: this.themes[this.theme].color },
                alpha: { value: this.themes[this.theme].alpha }
            },
            vertexShader: `attribute float scale;
                void main() {
                vec4 mvPosition = modelViewMatrix * vec4( position, 1.0 );
                gl_PointSize = scale * ( 300.0 / - mvPosition.z );
                gl_Position = projectionMatrix * mvPosition;
            }`,
            fragmentShader: `uniform vec3 color; uniform float alpha;
                void main() {
                if ( length( gl_PointCoord - vec2( 0.5, 0.5 ) ) > 0.475 ) discard;
                    gl_FragColor = vec4( color, alpha );
                }`
        } );

        this.particles = new Points( geometry, material );
        this.scene.add( this.particles );
    }

    initScene() {
        this.init();
        this.render();
    }

    initCanvas() {
        this.wrapper = {};
        this.wrapper.el = document.getElementById('scene-wrapper--hero-wave');
        this.wrapper.width = this.wrapper.el.offsetWidth;
        this.wrapper.height = this.wrapper.el.offsetHeight;
        this.canvas = {};
        this.canvas.el = document.getElementById('scene-hero-wave');
        this.canvas.width = this.canvas.el.offsetWidth;
        this.canvas.height = this.canvas.el.offsetHeight;

        this.theme = this.canvas.el.dataset.waveTheme || 'default';

        this.observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => this.isVisible = entry.intersectionRatio > 0);
        });
        this.observer.observe(this.wrapper.el);
    }

    initWebGL() {
        this.scene = new Scene();

        this.camera = new PerspectiveCamera( 75, this.canvas.width / this.canvas.height, 1, 10000 );
        this.camera.position.z = 1500;
        this.camera.position.x = ( this.cameraX - this.camera.position.x );
        this.camera.position.y = ( this.cameraY - this.camera.position.y );
        this.camera.lookAt( this.scene.position );

        this.renderer = new WebGLRenderer({
            alpha: true,
            antialias: true,
            canvas: this.canvas.el
        });

        this.renderer.setPixelRatio(1.5)
        this.renderer.setSize(this.wrapper.width, this.wrapper.height);
        this.renderer.setClearColor(0x000000, 0);
    }

    render() {
        if(this.isVisible) {
            const positions = this.particles.geometry.attributes.position.array;
            const scales = this.particles.geometry.attributes.scale.array;
            let i = 0, j = 0;
            for ( let ix = 0; ix < this.amount_x; ix ++ ) {
                for ( let iy = 0; iy < this.amount_y; iy ++ ) {
                    positions[ i + 1 ] = ( Math.sin( ( ix + this.count ) * 0.3 ) * 50 ) +
                        ( Math.sin( ( iy + this.count ) * 0.5 ) * 50 );
                    scales[ j ] = ( Math.sin( ( ix + this.count ) * 0.3 ) + 1 ) * 20 +
                        ( Math.sin( ( iy + this.count ) * 0.5 ) + 1 ) * 20;
                    i += 3;
                    j ++;
                }
            }

            this.particles.geometry.attributes.position.needsUpdate = true;
            this.particles.geometry.attributes.scale.needsUpdate = true;
            this.count += 0.008;
            this.particles.rotation.y += 0.00008;
        }
        this.renderer.render(this.scene, this.camera);
        window.requestAnimationFrame(this.render);
    }

    onResize () {
        this.wrapper.width = this.wrapper.el.offsetWidth;
        this.wrapper.height = this.wrapper.el.offsetHeight;
        this.wrapper.rect = this.wrapper.el.getBoundingClientRect();
        this.canvas.rect = this.canvas.el.getBoundingClientRect();

        this.camera.aspect = this.wrapper.width / this.wrapper.height;
        this.camera.updateProjectionMatrix();
        this.renderer.setSize( this.wrapper.width, this.wrapper.height );
        this.renderer.render(this.scene, this.camera);
    }


    initEvents() {
        window.addEventListener('resize', this.onResize, {passive: true});
    }

    bindMethods() {
        ['onResize', 'render']
            .forEach((fn) => this[fn] = this[fn].bind(this))
    }

}

export default FabricHeroWave
